import React from 'react';
import {
  ISeriesCardProps,
  isLargeCard,
  isSmallCard,
  hasRating,
  completedDate,
  determineFallbackImg,
  CARDTYPE,
} from '@ifit/mp-card-utilities';
import Card from '@ifit/mp-base-card';
import LineWrap from '@ifit/mp-line-wrap';
import ProgressiveBackground from '@ifit/mp-progressive-background';
import ProgressBar from '@ifit/mp-progress-bar';
import Intensity from '@ifit/mp-intensity';
import Chip from '@ifit/mp-chip';
import NativeClick, { METHODS } from '@ifit/mp-native-click';
import { withTranslation, useTranslation } from '@ifit/rapport-webview';
import Icon from '@ifit/react-icons/dist/components/icon';
import LONG_ARROW_RIGHT_LINE from '@ifit/react-icons/dist/icons/LONG_ARROW_RIGHT_LINE';
import cx from 'classnames';
import { withTheme } from '@ifit/themes/withTheme';
import rapportPackage from './rapport-web.json';

import './index.scss';

export const SeriesWorkout = (props: ISeriesCardProps) => {
  const {
    cardSize,
    data,
    locked,
    programInfo,
    disableNative,
    verbose,
    forceFallbackImg,
    isChallenge,
    lineWrapBasedOn,
    recommendationLabel,
  } = props;
  const { locale, _translate } = useTranslation();
  const { image, rating, title, intensity, lastCompleted, workouts } = data;

  let { fallbackImage } = props;

  if (!fallbackImage) {
    fallbackImage = determineFallbackImg(null, CARDTYPE.SERIES);
  }

  const small = isSmallCard(cardSize);
  const large = isLargeCard(cardSize);
  const displayRating = hasRating(rating);
  const completed = completedDate(lastCompleted);

  const viewSmall = _translate('View', {
    context:
      'Shown on an action button that opens up a view to show more information about a series or challenge.',
  });
  const viewChallenge = _translate('View challenge', {
    context: 'Click the action button to view the next challenge',
  });
  const viewSeriesText = _translate('View series', {
    context: 'Click the action button to view the next series',
  });
  const numberOfWorkoutsLabel = count =>
    _translate('%{count} Workout', {
      plural: '%{count} Workouts',
      context:
        'Label to tell the user how many workouts are in a series. A series is a set of related workouts that the user completes over several weeks.',
      count,
    });
  const footerInfo = isChallenge ? viewChallenge : viewSeriesText;
  const viewSeries = small ? viewSmall : footerInfo;

  return (
    <Card
      className={cx('card-template', 'series-card', {
        large,
        small,
      })}
    >
      <>
        <div className="thumbnail-container">
          <ProgressiveBackground
            image={!forceFallbackImg ? image : undefined}
            fallbackImage={fallbackImage}
            lazy={{ load: false }}
          />
          {locked && !recommendationLabel && (
            <div className="locked-chip">
              <Chip>
                <Card.Locked />
              </Chip>
            </div>
          )}
          {completed && !programInfo && !(small && locked) && !recommendationLabel && (
            <div className="completed-chip">
              <Chip>
                <span>
                  {small
                    ? _translate('COMPLETED', {
                        context:
                          'Signifies that a workout has been completed to user',
                      })
                    : _translate('COMPLETED:', {
                        context:
                          'Signifies that a workout has been completed to user. We will show the date of completion after the ":".',
                      })}{' '}
                </span>
                {!small && completed}
              </Chip>
            </div>
          )}
          {!completed && workouts.length !== 0 && !(small && locked) && !recommendationLabel && (
            <div className="chip-overlay">
              <Chip>{numberOfWorkoutsLabel(workouts.length)}</Chip>
            </div>
          )}
          {recommendationLabel && (
            <div className="recommendation-chip">
              <Chip recommended locked={locked}>
                {recommendationLabel}
              </Chip>
            </div>
          )}
        </div>
        <div className="details">
          {!small && <Intensity intensity={intensity} />}
          <LineWrap
            className={cx('title', { 'workout-visible': programInfo })}
            maxLines={2}
            text={title}
            basedOn={lineWrapBasedOn}
          />
          {data && programInfo && (
            <Card.WorkoutNumber programInfo={programInfo} />
          )}
          {data && programInfo ? (
            <NativeClick
              disableNative={disableNative}
              verbose={verbose}
              objectToSend={METHODS.programSelected(programInfo.seriesId)}
            >
              <Card.ContinueSeriesInfo
                small={small}
                isSeries
                programInfo={programInfo}
                locale={locale}
                locked={locked}
              />
            </NativeClick>
          ) : (
            <Card.Footer>
              <div
                className={cx('view-series', {
                  'align-left': !displayRating,
                })}
              >
                <span className="view-title">{viewSeries}</span>
                <Icon
                  icon={LONG_ARROW_RIGHT_LINE}
                  size={small ? 16 : 24}
                  color={null}
                />
              </div>
              {displayRating && (
                <Card.Rating rating={rating} size={0.65} unitSize={'em'} />
              )}
            </Card.Footer>
          )}
        </div>
        {completed && !programInfo && <ProgressBar progress={100} />}
      </>
    </Card>
  );
};

export default withTheme(withTranslation(rapportPackage)(SeriesWorkout));
